/**
 * DriverChatPage - Chat entre motorista e central
 * Interface mobile-first para comunicacao bidirecional
 */

import {
 useState, useCallback, useEffect, useRef 
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  Paper,
  Chip,
  Avatar,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Send as SendIcon,
  AttachFile as AttachIcon,
  SupportAgent as AgentIcon,
  Person as DriverIcon,
  AccessTime as TimeIcon,
  CheckCircle as ReadIcon,
  Schedule as PendingIcon,
  LocationOn as LocationIcon,
  Warning as AlertIcon,
  DirectionsCar as CarIcon,
} from '@mui/icons-material';
import { usePremiumTokens } from '../common/theme/usePremiumTokens';
import { useTranslation } from '../common/components/LocalizationProvider';
import { useEffectAsync } from '../reactHelper';

// Mensagens rapidas pre-definidas
const QUICK_MESSAGES = [
  { id: 'arrived', text: 'Cheguei no local', icon: LocationIcon },
  { id: 'delay', text: 'Vou atrasar', icon: TimeIcon },
  { id: 'absent', text: 'Cliente ausente', icon: AlertIcon },
  { id: 'help', text: 'Preciso de ajuda', icon: AlertIcon },
  { id: 'traffic', text: 'Transito intenso', icon: CarIcon },
];

// Formatar timestamp
const formatMessageTime = (timestamp) => {
  const date = new Date(timestamp);
  const now = new Date();
  const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  }
  if (diffDays === 1) {
    return `Ontem ${date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
  }
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
};

// Componente de mensagem
const MessageBubble = ({ message, isOwn, tokens }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: isOwn ? 'flex-end' : 'flex-start',
      mb: 1.5,
    }}
  >
    {!isOwn && (
      <Avatar
        sx={{
          width: 32,
          height: 32,
          mr: 1,
          backgroundColor: tokens?.accent?.primary,
        }}
      >
        <AgentIcon sx={{ fontSize: 18 }} />
      </Avatar>
    )}
    <Box sx={{ maxWidth: '75%' }}>
      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          borderRadius: 2,
          backgroundColor: isOwn ? tokens?.accent?.primary : tokens?.surface?.raised,
          color: isOwn ? '#fff' : tokens?.text?.primary,
          borderTopRightRadius: isOwn ? 4 : 16,
          borderTopLeftRadius: isOwn ? 16 : 4,
        }}
      >
        <Typography sx={{ fontSize: '14px', whiteSpace: 'pre-wrap' }}>
          {message.text}
        </Typography>
      </Paper>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: isOwn ? 'flex-end' : 'flex-start',
          gap: 0.5,
          mt: 0.5,
          px: 0.5,
        }}
      >
        <Typography sx={{ fontSize: '11px', color: tokens?.text?.tertiary }}>
          {formatMessageTime(message.timestamp)}
        </Typography>
        {isOwn && (
          message.read
            ? <ReadIcon sx={{ fontSize: 14, color: tokens?.semantic?.success }} />
            : <PendingIcon sx={{ fontSize: 14, color: tokens?.text?.tertiary }} />
        )}
      </Box>
    </Box>
    {isOwn && (
      <Avatar
        sx={{
          width: 32,
          height: 32,
          ml: 1,
          backgroundColor: tokens?.surface?.elevated,
        }}
      >
        <DriverIcon sx={{ fontSize: 18, color: tokens?.text?.secondary }} />
      </Avatar>
    )}
  </Box>
);

// Separador de data (reservado para uso futuro)
const _DateSeparator = ({ date, tokens }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      my: 2,
    }}
  >
    <Divider sx={{ flex: 1 }} />
    <Typography
      sx={{
        px: 2,
        fontSize: '12px',
        color: tokens?.text?.tertiary,
        backgroundColor: tokens?.surface?.base,
      }}
    >
      {date}
    </Typography>
    <Divider sx={{ flex: 1 }} />
  </Box>
);

const DriverChatPage = () => {
  const navigate = useNavigate();
  const { routeId } = useParams();
  const t = useTranslation();
  const tokens = usePremiumTokens();

  const messagesEndRef = useRef(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [driverId, setDriverId] = useState(null);

  // Carregar driver ID
  useEffectAsync(async () => {
    try {
      const response = await fetch('/api/session');
      if (response.ok) {
        const session = await response.json();
        setDriverId(session.id);
      }
    } catch (error) {
      console.error('Failed to get session:', error);
    }
  }, []);

  // Carregar mensagens
  useEffectAsync(async () => {
    if (!driverId) return;

    setLoading(true);
    try {
      // Mark messages as read
      fetch('/api/messages/driver/read', { method: 'PUT' }).catch(() => {});

      // Buscar mensagens do backend
      const url = routeId
        ? `/api/messages/driver?routeId=${routeId}`
        : '/api/messages/driver';

      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setMessages(data);
      } else {
        // Mock data para desenvolvimento
        setMessages([
          {
            id: 1,
            text: 'Ola! Como posso ajudar?',
            timestamp: new Date(Date.now() - 3600000).toISOString(),
            senderType: 'dispatcher',
            read: true,
          },
          {
            id: 2,
            text: 'Preciso de informacoes sobre a rota de hoje',
            timestamp: new Date(Date.now() - 3500000).toISOString(),
            senderType: 'driver',
            read: true,
          },
          {
            id: 3,
            text: 'Sua rota de hoje tem 8 entregas. A primeira parada e na Av. Paulista, 1000.',
            timestamp: new Date(Date.now() - 3400000).toISOString(),
            senderType: 'dispatcher',
            read: true,
          },
        ]);
      }
    } catch (error) {
      console.error('Failed to load messages:', error);
    } finally {
      setLoading(false);
    }
  }, [driverId, routeId]);

  // Scroll para ultima mensagem
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Enviar mensagem
  const handleSend = useCallback(async () => {
    if (!newMessage.trim() || sending) return;

    setSending(true);
    const messageText = newMessage.trim();
    setNewMessage('');

    try {
      const response = await fetch('/api/messages/driver', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: messageText,
          routePlanId: routeId || null,
        }),
      });

      if (response.ok) {
        const sent = await response.json();
        setMessages((prev) => [...prev, sent]);
      } else {
        // Adicionar localmente para UX
        setMessages((prev) => [...prev, {
          id: Date.now(),
          text: messageText,
          timestamp: new Date().toISOString(),
          senderType: 'driver',
          read: false,
        }]);
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      // Ainda adicionar localmente
      setMessages((prev) => [...prev, {
        id: Date.now(),
        text: messageText,
        timestamp: new Date().toISOString(),
        senderType: 'driver',
        read: false,
      }]);
    } finally {
      setSending(false);
    }
  }, [newMessage, sending, routeId, driverId]);

  // Enviar mensagem rapida
  const handleQuickMessage = useCallback((quickMsg) => {
    setNewMessage(quickMsg.text);
  }, []);

  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: tokens?.surface?.base,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          backgroundColor: tokens?.surface?.raised,
          borderBottom: `1px solid ${tokens?.border?.subtle}`,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <IconButton onClick={() => navigate(-1)}>
          <BackIcon sx={{ color: tokens?.text?.secondary }} />
        </IconButton>
        <Avatar sx={{ backgroundColor: tokens?.accent?.primary }}>
          <AgentIcon />
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 600, fontSize: '16px', color: tokens?.text?.primary }}>
            {t('chatWithCentral') || 'Central de Operacoes'}
          </Typography>
          <Typography sx={{ fontSize: '12px', color: tokens?.semantic?.success }}>
            Online
          </Typography>
        </Box>
      </Box>

      {/* Messages */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: 2,
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} sx={{ color: tokens?.accent?.primary }} />
          </Box>
        ) : messages.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <AgentIcon sx={{ fontSize: 48, color: tokens?.text?.tertiary, mb: 1 }} />
            <Typography sx={{ color: tokens?.text?.secondary, fontSize: '14px' }}>
              Nenhuma mensagem ainda
            </Typography>
            <Typography sx={{ color: tokens?.text?.tertiary, fontSize: '12px' }}>
              Envie uma mensagem para iniciar a conversa
            </Typography>
          </Box>
        ) : (
          <>
            {messages.map((message) => (
              <MessageBubble
                key={message.id}
                message={message}
                isOwn={message.senderType === 'driver'}
                tokens={tokens}
              />
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </Box>

      {/* Quick Messages */}
      <Box
        sx={{
          px: 2,
          py: 1,
          borderTop: `1px solid ${tokens?.border?.subtle}`,
          backgroundColor: tokens?.surface?.raised,
          overflowX: 'auto',
          whiteSpace: 'nowrap',
        }}
      >
        <Box sx={{ display: 'flex', gap: 1 }}>
          {QUICK_MESSAGES.map((qm) => {
            const Icon = qm.icon;
            return (
              <Chip
                key={qm.id}
                icon={<Icon sx={{ fontSize: 16 }} />}
                label={qm.text}
                size="small"
                onClick={() => handleQuickMessage(qm)}
                sx={{
                  backgroundColor: tokens?.surface?.elevated,
                  '&:hover': {
                    backgroundColor: tokens?.accent?.subtle,
                  },
                }}
              />
            );
          })}
        </Box>
      </Box>

      {/* Input */}
      <Box
        sx={{
          p: 2,
          backgroundColor: tokens?.surface?.raised,
          borderTop: `1px solid ${tokens?.border?.subtle}`,
        }}
      >
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
          <IconButton size="small" sx={{ color: tokens?.text?.tertiary }}>
            <AttachIcon />
          </IconButton>
          <TextField
            fullWidth
            multiline
            maxRows={4}
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            placeholder="Digite sua mensagem..."
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: tokens?.surface?.base,
                borderRadius: 3,
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleSend}
                    disabled={!newMessage.trim() || sending}
                    sx={{
                      color: newMessage.trim() ? tokens?.accent?.primary : tokens?.text?.tertiary,
                    }}
                  >
                    {sending ? (
                      <CircularProgress size={20} />
                    ) : (
                      <SendIcon />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default DriverChatPage;
